// Kassen eines Standorts: Liste mit Schichtzustand, Einrichtung, Schnelltasten – und was der Kassenbildschirm beim
// Start braucht. Die Kasse läuft im Schulungs- und Demomodus (lib/labels.ts: POS_BANNER).
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zTillOffline } from '../lib/offline/types'
import { zAmountCentsOrZero, zLocationRef, zPerson, zRegisterRef } from './common'
import { zBasketView, zFiscalMode } from './views'

const READ = 'pos.shift.read'
const MANAGE = 'pos.register.manage'
const SELL = 'pos.sale.create'

const zCode = z
  .string({ error: 'Bitte ein Kürzel eingeben.' })
  .trim()
  .min(2, { error: 'Das Kürzel hat mindestens 2 Zeichen.' })
  .max(12, { error: 'Das Kürzel hat höchstens 12 Zeichen.' })
  .regex(/^[A-Za-z0-9][A-Za-z0-9-]*$/, { error: 'Das Kürzel besteht aus Buchstaben, Ziffern und Bindestrich (es steht in jeder Bonnummer).' })

const zName = z.string({ error: 'Bitte einen Namen eingeben.' }).trim().min(2, { error: 'Bitte einen Namen eingeben.' }).max(80, { error: 'Der Name ist zu lang (höchstens 80 Zeichen).' })

export const zOpenShift = z.object({
  id: zId,
  businessDate: z.string(),
  openedAt: z.date(),
  openedBy: zPerson,
  floatCents: z.int(),
})

export const zRegisterRow = z.object({
  id: zId,
  code: z.string(),
  name: z.string(),
  location: zLocationRef,
  active: z.boolean(),
  /** übliches Wechselgeld beim Öffnen */
  floatCents: z.int(),
  /** Kassendifferenz, ab der der Abschluss freigegeben werden muss */
  diffLimitCents: z.int(),
  quickKeyCount: z.int(),
  version: z.int(),
  openShift: zOpenShift.nullable(),
})

export const registerList = defineContract({
  name: 'pos.register.list',
  title: 'Kassen anzeigen',
  description: 'Kassen der Märkte mit dem Zustand ihrer Schicht (offen seit, von wem).',
  llm: {
    description:
      'Listet die Kassen der Standorte, an denen die Person Schichten lesen darf: Kürzel, Name, Standort, ob eine ' +
      'Schicht offen ist (seit wann, von wem, mit welchem Wechselgeld in Cent), übliches Wechselgeld und die ' +
      'Differenzgrenze in Cent. Verwenden für „welche Kasse ist offen“. Umsätze liefert pos.shift.overview, einzelne ' +
      'Schichten pos.shift.list. Die Kasse läuft im Schulungs- und Demomodus.',
    keywords: ['Kasse', 'Kassen', 'offene Kasse', 'Schicht offen', 'Wechselgeld'],
  },
  input: z.strictObject({ locationId: zId.optional(), includeInactive: z.boolean().default(false) }),
  output: z.object({ rows: z.array(zRegisterRow) }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const registerSave = defineContract({
  name: 'pos.register.save',
  title: 'Kasse einrichten',
  description: 'Legt eine Kasse an einem Standort an oder ändert Namen, Wechselgeld und Differenzgrenze.',
  llm: {
    description:
      'Legt eine Kasse an (ohne `id`) oder ändert sie (mit `id`): Standort, Kürzel (steht in jeder Bonnummer und ' +
      'lässt sich nach dem ersten Bon nicht mehr ändern), Name, übliches Wechselgeld und die Differenzgrenze in Cent, ' +
      'ab der ein Abschluss freigegeben werden muss. Schnelltasten setzt pos.register.setQuickKeys. Öffnet keine Schicht.',
    keywords: ['Kasse anlegen', 'Kasse einrichten', 'Differenzgrenze', 'Wechselgeld festlegen'],
  },
  input: z.strictObject({
    id: zId.optional(),
    locationId: zId,
    code: zCode,
    name: zName,
    active: z.boolean().default(true),
    floatCents: zAmountCentsOrZero.default(15_000),
    diffLimitCents: zAmountCentsOrZero.default(500),
    expectedVersion: z.int().positive().optional(),
  }),
  output: z.object({ id: zId, version: z.int(), message: z.string() }),
  permission: MANAGE,
  risk: 'write',
  scope: (input) => [input.locationId],
})

export const registerSetQuickKeys = defineContract({
  name: 'pos.register.setQuickKeys',
  title: 'Schnelltasten belegen',
  description: 'Legt fest, welche Artikel und Gebinde eine Kasse als Schnelltasten zeigt.',
  llm: {
    description: 'Ersetzt die Schnelltasten einer Kasse: Liste aus Artikel und Gebinde in der Reihenfolge der Tasten (höchstens 24). Nur über die Einstellungen der Kasse.',
    keywords: ['Schnelltasten', 'Favoriten der Kasse'],
  },
  input: z.strictObject({
    registerId: zId,
    keys: z.array(z.strictObject({ itemId: zId, unitId: zId })).max(24, { error: 'Eine Kasse zeigt höchstens 24 Schnelltasten.' }),
  }),
  output: z.object({ id: zId, quickKeyCount: z.int(), message: z.string() }),
  permission: MANAGE,
  risk: 'write',
  scope: 'record',
  assistant: 'never',
})

export const zQuickKey = z.object({
  itemId: zId,
  unitId: zId,
  name: z.string(),
  unitLabel: z.string(),
  priceCents: z.int().nullable(),
  depositCents: z.int(),
  minAge: z.int(),
  promotion: z.boolean(),
})

export const registerQuickKeys = defineContract({
  name: 'pos.register.quickKeys',
  title: 'Schnelltasten anzeigen',
  description: 'Schnelltasten einer Kasse mit Namen, Gebinde und Preis.',
  llm: {
    description: 'Liefert die Schnelltasten einer Kasse: Artikel, Gebinde und heutiger Preis in Cent. Für die Einstellungen der Kasse; Preise allgemein liefert pricing.price.quote.',
    keywords: ['Schnelltasten'],
  },
  input: z.strictObject({ registerId: zId }),
  output: z.object({ keys: z.array(zQuickKey) }),
  permission: READ,
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})

export const zOpenBasket = z.object({
  id: zId,
  parked: z.boolean(),
  note: z.string().nullable(),
  totalCents: z.int(),
  lineCount: z.int(),
  operatorName: z.string().nullable(),
  startedAt: z.date(),
})

/** Alles, was der Kassenbildschirm beim Start braucht – eine Anfrage. */
export const tillState = defineContract({
  name: 'pos.till.state',
  title: 'Kasse starten',
  description: 'Zustand einer Kasse für den Verkaufsbildschirm: Schicht, laufender Bon, geparkte Bons, Schnelltasten, Grenzen der Person.',
  llm: { description: 'Lädt den Zustand einer Kasse für den Verkaufsbildschirm. Nur für die Oberfläche der Kasse.', keywords: ['Kasse starten'] },
  input: z.strictObject({ registerId: zId }),
  output: z.object({
    register: zRegisterRef.extend({ floatCents: z.int(), diffLimitCents: z.int(), active: z.boolean() }),
    shift: zOpenShift.nullable(),
    /** Kennzeichnung des Signatursatzes (Demo, Testumgebung, Schulung) – null im Echtbetrieb */
    fiscalNotice: z.string().nullable(),
    /** Betriebsart, Hinweis am Bildschirm und ein laufender Ausfall der Signatureinheit */
    fiscal: z.object({
      mode: zFiscalMode,
      /** Pflichthinweis am Bildschirm – null im Echtbetrieb */
      banner: z.string().nullable(),
      unitSerial: z.string(),
      clientSerial: z.string().nullable(),
      outage: z.object({ since: z.date(), reason: z.string() }).nullable(),
    }),
    basket: zBasketView.nullable(),
    openBaskets: z.array(zOpenBasket),
    quickKeys: z.array(zQuickKey),
    operator: z.object({
      id: zId,
      name: z.string(),
      /** Rabattgrenze in Basispunkten; null = unbegrenzt, 0 = kein Rabattrecht */
      maxDiscountBp: z.int().nullable(),
      canVoid: z.boolean(),
      canMoveCash: z.boolean(),
      canCount: z.boolean(),
      canReturn: z.boolean(),
      canOpen: z.boolean(),
    }),
    today: z.string(),
    /**
     * Kasse ohne Verbindung (docs/kasse-offline/SPEZIFIKATION.md §14.2): Freigabe, Gerät, Grenzen, nächste
     * Vorgangsnummer. Fehlt das Feld, kennt der Server den Betrieb ohne Verbindung noch nicht (wie eine Kasse ohne Gerät).
     */
    offline: zTillOffline.optional(),
  }),
  permission: SELL,
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})

export const zCatalogUnit = z.object({
  unitId: zId,
  code: z.string(),
  short: z.string(),
  label: z.string(),
  baseQty: z.int(),
  priceCents: z.int().nullable(),
  depositCents: z.int(),
  promotion: z.boolean(),
  isDefault: z.boolean(),
})

export const zCatalogHit = z.object({
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  minAge: z.int(),
  /** Bestand am Standort der Kasse in Tausendstel der Basiseinheit */
  onHand: z.int().nullable(),
  units: z.array(zCatalogUnit),
})

export const catalogFind = defineContract({
  name: 'pos.catalog.find',
  title: 'Artikel an der Kasse suchen',
  description: 'Sucht verkäufliche Artikel für die Kasse: nach Name, Artikelnummer oder Barcode, mit Preis je Gebinde.',
  llm: { description: 'Sucht Artikel für den Verkaufsbildschirm der Kasse. Nur für die Oberfläche; Artikel allgemein liefert masterdata.item.list.', keywords: ['Artikel an der Kasse'] },
  input: z.strictObject({
    registerId: zId,
    q: z.string().trim().max(120).optional(),
    /** gescannter oder getippter Barcode – trifft genau ein Gebinde */
    code: z.string().trim().max(80).optional(),
    partnerId: zId.nullish(),
  }),
  output: z.object({
    hits: z.array(zCatalogHit),
    /** bei `code`: das getroffene Gebinde */
    scanned: z.object({ itemId: zId, unitId: zId }).nullable(),
  }),
  permission: SELL,
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})
